import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  GetTeesheetBookingFee,
  IPutTeesheetBookingFee,
  PutBookingFeeCustomerType,
  PutTeesheetBookingFee,
  TeesheetBookingFee,
} from "api/rpc/2024-04/facilityAdmin/teesheet/bookingFee";

import { showError } from "redux/actions/ui";
import { ignoreOrderCompare, unsavedChangesExist } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";

import BookingFeeFormFields from "./components/BookingFeeFormFields";
import BookingFeeCustomerTypesTable from "./components/BookingFeeCustomerTypesTable";
import {
  applyFormValuesToBookingFee,
  bookingFeeToFormValues,
  initialBookingFeeFormValues,
  TBookingFeeFormValues,
} from "./bookingFeeForm";
import { formatBookingFeeProductLabel } from "./hooks/useBookingFeeProducts";
import { useBookingFeeCustomerTypes } from "./hooks/useBookingFeeCustomerTypes";

import "./bookingFees.scss";

const initialBookingFee: TeesheetBookingFee = {
  id: 0,
  booking_window_id: null,
  product: undefined,
  variant: undefined,
  price: 0,
  taxable: false,
  unit: "player",
  non_refundable: false,
  days_in_advance_start: "",
  days_in_advance_end: "",
  customer_types: undefined,
};

export default function BookingFee() {
  const { feeId } = useParams<{ feeId: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customerTypes = useBookingFeeCustomerTypes();

  const [bookingFeePreserved, setBookingFeePreserved] = useState<TeesheetBookingFee>(initialBookingFee);
  const [bookingFee, setBookingFee] = useState<TeesheetBookingFee>(initialBookingFee);
  const [formValues, setFormValues] = useState<TBookingFeeFormValues>(initialBookingFeeFormValues);

  const isFeeLoaded = bookingFee.id > 0;

  const feeProductLabel = useMemo(() => {
    if (!bookingFee.product || !bookingFee.variant) {
      return bookingFee.product_title ?? t("Booking fee");
    }

    return formatBookingFeeProductLabel(bookingFee.product, bookingFee.variant);
  }, [bookingFee.product, bookingFee.variant, bookingFee.product_title, t]);

  const hasUnsavedCustomerTypeChanges = !ignoreOrderCompare(
    customerTypes.selectedIds,
    bookingFeePreserved.customer_types?.map(type => type?.id).filter((id): id is number => id != null) ?? [],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadBookingFee(source.token);

    return () => {
      source.cancel();
    };
  }, [feeId]);

  async function loadBookingFee(token?: CancelToken) {
    const res = await GetTeesheetBookingFee({ id: parseInt(feeId, 10), extended: true }, !token, token);

    if (token?.reason) {
      return;
    }

    if (Number(res.status) !== Number(StatusCode.OK)) {
      return;
    }

    const feeData = res.data?.[0];
    if (!feeData) {
      return;
    }

    const fee: TeesheetBookingFee = {
      ...feeData,
      customer_types: feeData.customer_types ?? [],
    };

    setBookingFee(fee);
    setBookingFeePreserved(fee);
    setFormValues(bookingFeeToFormValues(fee));
    customerTypes.setSelection(
      fee.customer_types?.map(type => type?.id).filter((id): id is number => id != null) ?? [],
    );
  }

  async function updateBookingFee() {
    const variantId = bookingFee.variant?.id;
    if (!variantId) {
      return;
    }

    const params: IPutTeesheetBookingFee = {
      id: parseInt(feeId, 10),
      variant_id: variantId,
      non_refundable: bookingFee.non_refundable ?? false,
      days_in_advance_start: parseInt(String(bookingFee.days_in_advance_start ?? ""), 10),
      days_in_advance_end: parseInt(String(bookingFee.days_in_advance_end ?? ""), 10),
    };

    const res = await PutTeesheetBookingFee(params, true);

    if (Number(res.status) !== Number(StatusCode.OK)) {
      dispatch(showError(res.data as string));
    }
  }

  async function updateCustomerTypes() {
    const res = await PutBookingFeeCustomerType(
      { booking_fee_id: parseInt(feeId, 10), customer_type_ids: customerTypes.selectedIds },
      true,
    );

    if (Number(res.status) !== Number(StatusCode.OK)) {
      return;
    }

    const wantedCustomerTypes =
      customerTypes.types?.filter(
        type => type?.id != null && customerTypes.selectedIds.includes(type.id),
      ) ?? [];

    setBookingFee(prev => ({ ...prev, customer_types: [...wantedCustomerTypes] }));
    setBookingFeePreserved(prev => ({ ...prev, customer_types: [...wantedCustomerTypes] }));
  }

  function handleUpdate() {
    if (hasUnsavedCustomerTypeChanges) {
      void updateCustomerTypes();
    }

    if (unsavedChangesExist(bookingFee, bookingFeePreserved)) {
      void updateBookingFee();
    }

    setBookingFeePreserved(prev => ({ ...prev, ...bookingFee }));
  }

  function handleReset() {
    setBookingFee(bookingFeePreserved);
    setFormValues(bookingFeeToFormValues(bookingFeePreserved));
    customerTypes.setSelection(
      bookingFeePreserved.customer_types?.map(type => type?.id).filter((id): id is number => id != null) ?? [],
    );
    customerTypes.setSearch("");
  }

  function handleFormChange(setter: React.SetStateAction<TBookingFeeFormValues>) {
    setFormValues(prev => {
      const next = typeof setter === "function" ? setter(prev) : setter;

      const product =
        next.product?.id === bookingFeePreserved.product?.id && bookingFeePreserved.product
          ? { ...bookingFeePreserved.product }
          : next.product ?? undefined;
      const variant =
        next.variant?.id === bookingFeePreserved.variant?.id && bookingFeePreserved.variant
          ? { ...bookingFeePreserved.variant }
          : next.variant ?? undefined;

      const valuesWithPreservedProduct = {
        ...next,
        product: product ?? null,
        variant: variant ?? null,
      };

      setBookingFee(fee => applyFormValuesToBookingFee(fee, valuesWithPreservedProduct));

      return next;
    });
  }

  return (
    <Page
      title={t("Edit booking fee")}
      breadcrumbs={[
        { label: t("Booking fees"), url: "/admin/settings/tee-sheet/booking-fees" },
        { label: feeProductLabel },
      ]}
      notificationBarProps={{
        isVisible:
          (unsavedChangesExist(bookingFee, bookingFeePreserved) || hasUnsavedCustomerTypeChanges) &&
          bookingFee.days_in_advance_start !== "" &&
          bookingFee.days_in_advance_end !== "",
        onAction: () => handleUpdate(),
        onCancel: () => handleReset(),
      }}
    >
      <Card>
        <Card.Section>
          <BookingFeeFormFields
            values={formValues}
            onChange={handleFormChange}
            hideCustomerTypesSection
            productSearchEnabled={isFeeLoaded}
            onCreateProduct={() => history.push("/admin/product/new")}
          />
        </Card.Section>
      </Card>

      <Card className="booking-fees__edit-card">
        <Card.Section>
          <div className="booking-fees">
            <div className="booking-fees__section">
              <div className="booking-fees__section-label">{t("Customer types")}</div>
              <div className="booking-fees__section-support">
                {t("Select the customer types this booking fee applies to.")}
              </div>

              <BookingFeeCustomerTypesTable
                layout="page"
                showSelectionMeta
                search={customerTypes.search}
                onSearchChange={customerTypes.setSearch}
                filteredTypes={customerTypes.filteredTypes}
                selectedIds={customerTypes.selectedIds}
                isLoading={customerTypes.isLoading || !isFeeLoaded}
                onToggleType={customerTypes.toggleType}
                onSelectAllToggle={customerTypes.toggleSelectAllFiltered}
                onSort={customerTypes.sortTypes}
                onClearAll={customerTypes.clearAll}
                onCreateCustomerType={() => history.push("/admin/settings/customer_types/new")}
                searchInputId="booking-fee-edit-customer-type-search"
              />
            </div>
          </div>
        </Card.Section>
      </Card>
    </Page>
  );
}
